// Phase I3 — Referee DNA panel.
// Maps super_acca_disciplinary → DNAStat tiles (cards/game, fouls/game,
// pen rate, red rate, home bias). When the row is missing or sample
// invalid we render the operator-facing "still ramping" copy.

import { Badge, DNAStat, Eyebrow } from "../../../components/super-acca";
import { CATEGORY } from "../../../super-acca/categories";
import type { DisciplinaryIntelView } from "../../../super-acca/view-models";

export interface RefereeDNAPanelProps {
  data: DisciplinaryIntelView | null;
}

export function RefereeDNAPanel({ data }: RefereeDNAPanelProps) {
  if (!data || !data.sample_valid) {
    const appts = data?.appointments ?? 0;
    return (
      <div
        style={{
          padding: 22,
          background: "var(--sa-bg-panel)",
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
        }}
      >
        <Eyebrow color={CATEGORY.DISCIPLINE.color}>Referee DNA</Eyebrow>
        <p
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            marginTop: 12,
            lineHeight: 1.6,
          }}
        >
          Referee DNA still ramping — {appts} appointments tracked. The
          disciplinary agent activates once the assigned referee has at least
          10 settled appointments in the dataset.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 22,
        background: "var(--sa-bg-panel)",
        border: "1px solid var(--sa-hairline)",
        borderRadius: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <Eyebrow color={CATEGORY.DISCIPLINE.color}>
            Referee DNA · {data.appointments ?? "—"} APPOINTMENTS
          </Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 28,
              fontWeight: 600,
              marginTop: 6,
              color: "var(--sa-fg-primary)",
            }}
          >
            {data.referee_name ?? "Referee unassigned"}
          </div>
        </div>
        {data.context_label && data.context_multiplier !== null ? (
          <Badge variant="danger">
            {data.context_label.toUpperCase()} · ×{data.context_multiplier}
          </Badge>
        ) : null}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 14,
          marginTop: 18,
        }}
      >
        <DNAStat label="CARDS / GAME" value={data.cards_avg ?? "—"} accent />
        <DNAStat label="FOULS / GAME" value={data.fouls_avg ?? "—"} />
        <DNAStat label="PEN RATE" value={data.pen_rate ?? "—"} />
        <DNAStat label="RED RATE" value={data.red_rate ?? "—"} />
        <DNAStat
          label="EXP. CARDS"
          value={data.expected_total_cards ?? "—"}
          sub={
            data.cards_avg !== null && data.context_multiplier !== null
              ? `Base ${data.cards_avg} × ${data.context_multiplier}`
              : null
          }
        />
      </div>
      {data.first_card_min !== null ? (
        <div
          style={{
            marginTop: 14,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          First card timing avg:{" "}
          <span style={{ color: "var(--sa-fg-primary)" }}>min {data.first_card_min}</span>.
        </div>
      ) : null}
    </div>
  );
}
